<script setup lang="ts">
import { watch } from "vue";
import { firstQueryValue } from "~/utils/firstQueryValue";

const makersStore = useMakersStore();
const productsStore = useProductsStore();
const settingsStore = useSettingsStore();
const deliveryStore = useDeliveryStore();
const uiStore = useUiStore();
const config = useRuntimeConfig();
const { track } = useTracking();

definePageMeta({
  layout: "default",
});

const siteOrigin = config.public.mainUrl as string;

useSeoMeta({
  title: "Find Office Catering in London | Browse &Dine Menus",
  description:
    "Looking for catering for your team? Explore &Dine's handpicked menus from London's top independent kitchens. Search by cuisine, occasion or dietary need.",
});

useHead({
  link: [{ rel: "canonical", href: `${siteOrigin}/search` }],
  bodyAttrs: { "data-page": "search" },
});

const route = useRoute();
const router = useRouter();

// After hydrate, hide the SSR maker grid in favour of the ClientOnly carousel.
const isHydrated = ref(false);

// How many maker cards each section puts into the server-rendered HTML.
//
// What the user actually sees is a `ClientOnly` carousel showing ~4 cards, so
// every card past the fourth was markup nobody ever looked at: the server
// rendered all of them as a full flex-wrap grid (334 cards / 760KB of HTML
// across the category set), and then the entire grid was `display: none`d the
// instant the carousels hydrated. That wall-of-cards-then-collapse is what made
// the page look unstyled/malformed on load. Four keeps a crawlable card and link
// per section at roughly the carousel's own height, so the swap is invisible.
const SSR_MAKER_LIMIT = 4;

// Everything the first paint needs, loaded as one unit.
//
// Deliberately NOT `useAsyncData`: its handler does not run on client-side
// navigation, so arriving here from a `<NuxtLink>` fetched nothing and the page
// hung behind its spinner forever (docs/decisions.md 2026-08-12).
//
// Banners belong in here too, not in `onMounted`. Fetching them after mount
// is why the page arrived malformed: the banner strip was absent from the
// server HTML entirely, so everything below it jumped down once it appeared.
const searchDataLoading = ref(false);

function loadSearchData() {
  const requests: Promise<unknown>[] = [];
  if (!makersStore.makerCategories) requests.push(makersStore.getMakerCategories());
  if (!productsStore.tags) requests.push(productsStore.getTags());
  if (!productsStore.cuisines) requests.push(productsStore.getCuisines());
  if (!settingsStore.loaded) requests.push(settingsStore.fetchBanners());

  // Hydrating after an SSR render: everything is already in the payload, so
  // don't raise the flag at all — the spinner must never replace content we hold.
  if (!requests.length) return Promise.resolve();

  searchDataLoading.value = true;
  // `allSettled` so one failed call can't tip the page into rendering a
  // half-built body, and `finally` so the flag is released whatever happens. A
  // flag cleared only on the happy path is exactly how this page stranded before.
  return Promise.allSettled(requests).finally(() => {
    searchDataLoading.value = false;
  });
}

// Awaited on the server so the HTML ships complete (banner, ratings, cards), but
// only *started* on the client. A top-level `await` suspends the route change,
// which is why a client-side navigation sat on the previous page with no
// feedback and the loading state below never rendered.
if (import.meta.server) {
  await loadSearchData();
} else {
  loadSearchData();
}

// Restore filter state from URL query params
const initialDate = computed(() => firstQueryValue(route.query.date) || "");
const initialTags = computed(() => {
  try {
    const raw = firstQueryValue(route.query.tags);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as (string | number)[]) : [];
  } catch {
    return [];
  }
});
const initialCuisines = computed(() => {
  try {
    const raw = firstQueryValue(route.query.cuisines);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as (string | number)[]) : [];
  } catch {
    return [];
  }
});

const searchQueryRef = ref<string>((route.query.q as string) || "");

function syncFiltersToUrl() {
  const query: Record<string, string> = {};
  if (selectedDate.value) query.date = selectedDate.value;
  if (appliedFilters.value && appliedFilters.value.tags?.length) {
    query.tags = JSON.stringify(appliedFilters.value.tags.map((t) => t.id));
  }
  if (appliedFilters.value && appliedFilters.value.cuisines?.length) {
    query.cuisines = JSON.stringify(appliedFilters.value.cuisines.map((c) => c.id));
  }
  // Preserve the search term in the URL — otherwise applying a filter
  // wipes `?q=` from the location bar, the next page mount re-reads an
  // empty query, and the deep-search results vanish silently.
  if (searchQueryRef.value && searchQueryRef.value.length > 2) {
    query.q = searchQueryRef.value;
  }
  router.replace({ query });
}

// When the user is already on /search and presses Enter in the NavBar SearchBar,
// navigateTo('/search?q=...') updates the URL without remounting the page, so
// onMounted never re-fires. This watcher catches that query change and triggers
// the search just as onMounted does for the initial landing.
watch(
  () => route.query.q,
  (q) => {
    const query = (q as string) || "";
    searchQueryRef.value = query;
    if (query.length > 2) {
      makersStore.deepSearch(query);
    } else {
      makersStore.filteredMakerCategories = false;
      makersStore.searchResultMakers = [];
    }
  },
);

onMounted(async () => {
  isHydrated.value = true;
  // Reset any persisted filter state from a previous visit
  makersStore.filteredMakerCategories = false;
  makersStore.searchResultMakers = [];
  makersStore.setAvailableMakers(false);
  deliveryStore.restoreDeliveryAddress();

  // If navigated here with a search query (e.g. from NavBar), trigger deep search
  const searchQuery = route.query.q as string;
  if (searchQuery && searchQuery.length > 2) {
    await makersStore.deepSearch(searchQuery);
  }
});

// Categories render in the order set from the Hub's Homepage Settings
// (backend `sortingIndex`). The API already returns them in that order.
const sortedMakerCategories = computed(() => makersStore.makerCategories || []);

const selectedDate = ref("");
const appliedFilters = ref<
  { tags?: { id: string | number }[]; cuisines?: { id: string | number }[] } | false
>(false);

async function handleDeepSearch(query: string) {
  // Track the canonical search term so syncFiltersToUrl can write/clear
  // `?q=` alongside the other filter params. Without this the URL keeps
  // a stale `q` from the previous search (or from the navbar-driven
  // landing) — every subsequent filter sync drops it back into the URL
  // and the deep-search results misalign with what's in the input.
  searchQueryRef.value = query || "";
  syncFiltersToUrl();
  track("search_performed", { query });
  await makersStore.deepSearch(query);
}

function handleFilterApply(
  payload: { tags?: { id: string | number }[]; cuisines?: { id: string | number }[] } | false,
  meta?: { occasionCount: number; attributeCount: number },
) {
  appliedFilters.value = payload;

  const hasFilters = payload && (payload.tags?.length || payload.cuisines?.length);

  if (hasFilters) {
    const hasTags = !!(payload as { tags?: unknown[] }).tags?.length;
    const hasCuisines = !!(payload as { cuisines?: unknown[] }).cuisines?.length;
    track("search_filtered", {
      source: "search",
      filter_type: hasTags && hasCuisines ? "combined" : hasTags ? "tags" : "cuisines",
      tag_count: (payload as { tags?: unknown[] }).tags?.length ?? 0,
      ...(meta ? { occasion_count: meta.occasionCount, attribute_count: meta.attributeCount } : {}),
      cuisine_count: (payload as { cuisines?: unknown[] }).cuisines?.length ?? 0,
    });
  }

  if (hasFilters) {
    uiStore.setFilterLoading(true);
    makersStore.filteredMakerCategories = makersStore.filteredMakerCategories || [];
    makersStore.deepFilter(payload);
  } else {
    // No occasion/cuisine filters: setFilteredMakers(false) restores the right
    // base set for every combination — search results (∩ date) when a search is
    // active, date-available makers, or the carousel landing (false).
    makersStore.setFilteredMakers(false);
  }

  syncFiltersToUrl();
}

async function handleDateFilter(date: string) {
  if (date) {
    track("search_filtered", { source: "search", filter_type: "date", date });
  }
  selectedDate.value = date;
  uiStore.setFilterLoading(true);
  makersStore.filteredMakerCategories = makersStore.filteredMakerCategories || [];

  const hasFilters =
    appliedFilters.value &&
    (appliedFilters.value.tags?.length || appliedFilters.value.cuisines?.length);

  if (date) {
    await makersStore.getAvailableMakers(date);
  } else {
    // Clear date constraint without wiping filteredMakerCategories when other filters are active
    makersStore.makerIdsByDate = [];
    if (!hasFilters) {
      // Restores search results when a search is active, else the carousel landing.
      makersStore.setFilteredMakers(false);
    }
  }

  if (hasFilters) {
    makersStore.deepFilter(appliedFilters.value);
  } else {
    uiStore.setFilterLoading(false);
  }

  syncFiltersToUrl();
}

async function handleRestoreFilters(payload: {
  date: string;
  tags: (string | number)[];
  cuisines: (string | number)[];
}) {
  // Sequentially: set date first (needs await for API), then apply category filters
  if (payload.date) {
    selectedDate.value = payload.date;
    uiStore.setFilterLoading(true);
    makersStore.filteredMakerCategories = makersStore.filteredMakerCategories || [];
    await makersStore.getAvailableMakers(payload.date);
  }

  const hasTagsOrCuisines = payload.tags.length > 0 || payload.cuisines.length > 0;
  if (hasTagsOrCuisines) {
    const filterPayload: {
      tags?: { id: string | number }[];
      cuisines?: { id: string | number }[];
    } = {};
    if (payload.tags.length) filterPayload.tags = payload.tags.map((id) => ({ id }));
    if (payload.cuisines.length) filterPayload.cuisines = payload.cuisines.map((id) => ({ id }));
    appliedFilters.value = filterPayload;
    uiStore.setFilterLoading(true);
    makersStore.deepFilter(filterPayload);
  } else {
    uiStore.setFilterLoading(false);
  }
}
</script>

<template>
  <section class="search">
    <!-- Spinner only when there is genuinely nothing to show: a stuck loading
         flag must never be able to hide a catalogue we already hold. -->
    <div
      v-if="searchDataLoading && !makersStore.makerCategories"
      class="search__loading"
    >
      <Loading />
    </div>

    <div v-else>
      <!-- Mobile-only: full-width search bar lives permanently in the
           page head (no nav toggle). Filters render alongside it below
           via the regular filter block — no sliders toggle on mobile.
           Shown/hidden purely in CSS: gating this on a JS `isMobile` flag
           meant the server-rendered paint was always the desktop layout, so
           mobile got no search bar and the wrong body padding until hydration. -->
      <div class="search__head">
        <div class="search__head--mobile-search">
          <SearchBar
            class="bar"
            @search-submit="handleDeepSearch"
          />
        </div>
      </div>
      <div class="search__body">
        <!-- Hub-managed promotional banners. Sits above the filter bar in both
             views (carousel landing and results) so the layout reads the same
             whether or not filters are active. Renders nothing until banners
             are configured in the Hub settings page. -->
        <HomepageBanners />
        <div class="search__body--filters">
          <SearchFilterBar
            :tags="productsStore.tags || []"
            :cuisines="productsStore.cuisines || []"
            :initial-tags="initialTags"
            :initial-cuisines="initialCuisines"
            :initial-date="initialDate"
            @filter="handleFilterApply"
            @date="handleDateFilter"
            @restore="handleRestoreFilters"
          />
        </div>
        <div
          v-if="!makersStore.filteredMakerCategories"
          class="search__body--main"
        >
          <div
            v-for="(section, i) in sortedMakerCategories"
            :key="`section_${i}`"
            class="section"
          >
            <div class="section__header">
              <div class="section__header--text">
                <NuxtLink
                  :to="`/search/${section.slug}`"
                  class="section__header--title"
                >
                  {{ section.name }}
                </NuxtLink>
                <p
                  class="grey-text"
                  v-html="section.description"
                ></p>
              </div>
            </div>
            <!-- SSR-visible maker grid so crawlers see cards without JS.
                 Hidden after hydrate in favour of the ClientOnly carousel.
                 Capped at SSR_MAKER_LIMIT so this grid is about as tall as the
                 carousel that replaces it — see the constant's note. -->
            <div
              class="section__ssr-makers"
              :class="{ 'section__ssr-makers--hydrated': isHydrated }"
            >
              <MakerList
                v-for="maker in section.makers.slice(0, SSR_MAKER_LIMIT)"
                :key="`ssr_${maker.slug}`"
                class="section__ssr-makers--item"
                :maker="maker"
                :is-lazy="true"
              />
            </div>
            <ClientOnly>
              <CarouselBase
                :breakpoints="[
                  [300, 1.2],
                  [768, 2],
                  [1000, 3],
                  [1300, 4],
                ]"
                :fade-edges="true"
                nav-position="top-right"
              >
                <CarouselSlide
                  v-for="maker in section.makers"
                  :key="maker.slug"
                  :width="`${100 / 4}%`"
                >
                  <MakerList
                    :maker="maker"
                    :is-lazy="true"
                  />
                </CarouselSlide>
              </CarouselBase>
            </ClientOnly>
          </div>
        </div>
        <div
          v-else
          class="search__body--results"
        >
          <Loading
            v-if="uiStore.filterLoading"
            :full-screen="false"
          />
          <h4
            v-else-if="makersStore.filteredMakerCategories.length === 0"
            class="count grey-text"
          >
            <fa :icon="['fal', 'magnifying-glass']" />
            No results
          </h4>
          <h4
            v-else
            class="count grey-text"
          >
            <fa :icon="['fal', 'magnifying-glass']" />
            {{ makersStore.filteredMakerCategories.length }} result{{
              makersStore.filteredMakerCategories.length > 1 ? "s" : ""
            }}
          </h4>
          <div class="items">
            <MakerList
              v-for="(maker, index) in makersStore.filteredMakerCategories"
              :key="`filtered_${index}`"
              class="items__item"
              :maker="maker"
              :is-lazy="true"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
.search {
  background-color: var(--canvas-warm);

  // Holds roughly a screen while the page data loads, so the spinner sits in the
  // middle of the viewport instead of tucked under the fixed navbar with the
  // footer pulled up behind it.
  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 70vh;
    padding-top: 5rem;
  }

  &__head {
    margin-top: 5rem;
    padding: 1rem 6% 1rem 25%;
    background-color: var(--canvas-warm);
    position: fixed;
    width: 69%;
    z-index: 7777;
    // Mobile-only bar. Hidden here rather than with a `v-if="isMobile"` so the
    // first paint is right on both viewports — the only child is
    // `--mobile-search`, so on desktop this would otherwise be an empty
    // bordered strip.
    display: none;
    justify-content: space-between;
    align-items: center;
    border: 2px solid var(--color-grey-3);
    @media (max-width: $tablet) {
      display: flex;
      align-items: stretch;
      border: none;
      flex-direction: column;
      margin-top: 5rem;
      padding: 0.75rem 2%;
      width: 96%;
    }
    &--bar {
      width: 35%;
    }
    &--selects {
      display: flex;
      align-items: center;
      width: 60%;
      justify-content: space-between;
      svg {
        margin-right: 1%;
      }
      @media (max-width: $tablet) {
        width: 100%;
      }
    }
    &--mobile-search {
      display: none;
      @media (max-width: $tablet) {
        display: block;
        .bar {
          width: 100%;
        }
      }
    }
  }
  &__body {
    padding: 5rem 5%;
    display: flex;
    flex-direction: column;
    @media (max-width: $tablet) {
      width: 96%;
      // Top padding clears the fixed `__head` search bar. This used to be an
      // inline `padding-top: 9rem` bound to the JS `isMobile` flag, so the
      // server-rendered paint had the full 15rem and snapped up on hydration.
      padding: 9rem 2% 15rem;
    }
    &--heading {
      margin: 0 0 1rem;
      font-size: 1.5rem;
      font-weight: var(--weight-light);
      letter-spacing: -0.02em;
    }
    &--main {
      width: 100%;
      padding: 0;
      --carousel-fade-color: var(--canvas-warm);
      @media (max-width: $tablet) {
        width: 100%;
        padding: 0;
      }
      .section__ssr-makers {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        margin-bottom: 1rem;

        // Written as a descendant of the grid, not as `&--item`, purely for
        // specificity. This class lands on the MakerList root, which also
        // carries MakerList's own scoped `.maker-list[data-v-…] { width: 95% }`
        // — the same (0,2,0) specificity as `.search__body--main
        // .section__ssr-makers--item`, and emitted later in the cascade, so it
        // won. Every card in this "grid" rendered full width, one per row: the
        // server HTML was a 55,000px single column of cards that collapsed to a
        // few carousels on hydration, which is what made the page look unstyled
        // on load. The extra compound puts this at (0,3,0) so it wins.
        //
        // Row counts per breakpoint match what the carousel replacing this grid
        // shows, so the swap doesn't change the page height.
        .section__ssr-makers--item {
          // Neutralise MakerList's own margin so `gap` alone does the spacing
          // and four cards tile the row exactly.
          margin: 0;
          width: calc(25% - 0.75rem);
          @media (max-width: $laptop) {
            width: calc(50% - 0.5rem);
            &:nth-child(n + 3) {
              display: none;
            }
          }
          @media (max-width: $tablet) {
            width: 100%;
            &:nth-child(n + 2) {
              display: none;
            }
          }
        }

        &--hydrated {
          display: none;
        }
      }
      .color {
        margin-top: 1rem;
        border-radius: 0.4rem;
        width: 95%;
      }
      .section,
      .bundles {
        position: relative;
        .carousel-container {
          position: relative;
        }
        .pink-text {
          margin: 1rem 0 0.5rem 0;
        }
        .grey-text {
          color: var(--color-grey-2) !important;
          margin: 0 0 0.375rem 0;
          width: 80%;
          @media (max-width: $tablet) {
            width: 100%;
          }
        }
        &__header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          &--text {
            width: 80%;
            @media (max-width: $tablet) {
              width: 100%;
            }
          }
          &--link {
            white-space: nowrap;
            font-size: 0.9rem;
            font-weight: var(--weight-semibold);
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.4rem;
            margin-top: 1.2rem;
            &:hover {
              text-decoration: underline;
            }
          }
          // Section header title: matches the global h1/h2 weight
          // tone so headers across the page read as the same family.
          &--title {
            display: inline;
            font-size: 1.5rem !important;
            font-weight: var(--weight-light) !important;
            color: var(--color-grey-1) !important;
            letter-spacing: -0.02em;
            text-decoration: none !important;
            color: var(--color-grey-1) !important;
            transition: color var(--dur-fast) var(--ease-out);
            cursor: pointer;
            &:hover {
              color: var(--color-pink-1) !important;
              text-decoration: none !important;
              cursor: pointer !important;
            }
          }
        }
      }
      .bundles {
        margin-bottom: 3rem;
      }
      .section {
        margin-top: 0.875rem;
        &:first-of-type {
          margin-top: 0;
        }
      }
    }
    &--filters {
      width: 100%;
      padding: 0;
      @media (max-width: $tablet) {
        width: 100%;
        padding: 0;
      }
    }
    &--results {
      width: 100%;
      padding: 1rem 0 0;
      // Empty-state safety net: when the results list is short (no
      // results, or just one or two), keep the section tall enough that
      // the footer doesn't slide up under the absolutely-positioned
      // filter dropdown. 28rem is just above the dropdown's 24rem cap
      // so the dropdown never reaches the footer.
      min-height: 28rem;

      @media (max-width: $tablet) {
        width: 100%;
        padding: 2rem 0;
      }
      .section {
        margin-top: 0;
        &__header {
          &--title {
            display: inline;
            font-size: 1.5rem !important;
            font-weight: var(--weight-light) !important;
            color: var(--color-grey-1) !important;
            letter-spacing: -0.02em;
            text-decoration: none !important;
            cursor: pointer;
            &:hover {
              text-decoration: underline;
            }
          }
        }
        @media (max-width: $tablet) {
          margin-top: -1rem;
          h2,
          p {
            margin: 0.5rem 2%;
          }
        }
      }
      .count {
        margin: 0.5rem 0 1.5rem;
        @media (max-width: $tablet) {
          margin: 0 5% 1.5rem;
        }
      }
      .items {
        flex-direction: row;
        flex-wrap: wrap;
        display: flex;
        justify-content: flex-start;
        align-content: flex-start;
        align-items: stretch;

        // Equal-height row: every card stretches to match the tallest in the
        // row. Reads as a uniform grid instead of a ragged staircase when
        // makers have different cuisine counts or rating vs NEW states.
        &__item {
          width: calc(25% - 0.5rem) !important;
          margin: 0 0.5rem var(--space-3) 0;
          display: flex;
          @media (max-width: $laptop) {
            width: 46% !important;
            margin: 2% 2% var(--space-3);
          }
          @media (max-width: $tablet) {
            width: 96% !important;
            margin: 2% 2% var(--space-3);
          }
        }
        &__item:nth-child(4n) {
          margin-right: 0;
        }
      }
    }
  }
}
</style>
